import { LitElement, html } from 'lit';

/**
`iron-collapse` creates a collapsible block of content.  By default, the content
will be collapsed.  Use `opened` or `toggle()` to show/hide the content.

    <button on-click="toggle">toggle collapse</button>

    <iron-collapse id="collapse">
      <div>Content goes here...</div>
    </iron-collapse>

    ...

    toggle: function() {
      this.$.collapse.toggle();
    }

`iron-collapse` adjusts the max-height/max-width of the collapsible element to
show/hide the content.  So avoid putting padding/margin/border on the
collapsible directly, and instead put a div inside and style that.

    <style>
      .collapse-content {
        padding: 15px;
        border: 1px solid #dedede;
      }
    </style>

    <iron-collapse>
      <div class="collapse-content">
        <div>Content goes here...</div>
      </div>
    </iron-collapse>

### Styling

The following custom properties and mixins are available for styling:

Custom property | Description | Default
----------------|-------------|----------
`--iron-collapse-transition-duration` | Animation transition duration | `300ms`

@group Iron Elements
@hero hero.svg
@demo demo/index.html
@element iron-collapse
*/
class IronCollapse extends LitElement {
  static get is() { return 'iron-collapse'; }

  render() {
    return html`
      <style>
        :host {
          display: block;
          transition-duration: var(--iron-collapse-transition-duration, 400ms);
          /* Safari 10 needs this property prefixed to correctly apply the custom property */
          -webkit-transition-duration: var(--iron-collapse-transition-duration, 400ms);
          overflow: visible;
        }
  
        :host(.iron-collapse-closed) {
          display: none;
        }
  
        :host(:not(.iron-collapse-opened)) {
          overflow: hidden;
        }
      </style>
  
      <slot></slot>
    `;
  }

  // is:
  // behaviors: [IronResizableBehavior],

  static get properties() {
    return {
      /**
       * If true, the orientation is horizontal; otherwise is vertical.
       *
       * @attribute horizontal
       */
      horizontal: {
        type: Boolean,
        // value: false, observer: '_horizontalChanged'
      },

      /**
       * Set opened to true to show the collapse element and to false to hide it.
       *
       * @attribute opened
       */
      opened: {
        type: Boolean,
        // , value: false, notify: true, observer: '_openedChanged'
      },

      /**
       * Set noAnimation to true to disable animations.
       *
       * @attribute noAnimation
       */
      noAnimation: {
        type: Boolean,
        attribute: 'no-animation',
      },
    };
  }

  constructor(props) {
    super(props);
    this.horizontal = false;
    this.opened = false;
    this.noAnimation = false;
    this._transitioning = false;
    this._desiredSize = '';
    this.setAttribute('role', 'group');
    this.setAttribute('aria-hidden', 'true');
    this.addEventListener('transitionend', this._onTransitionEnd);
  }

  get dimension() {
    return this.horizontal ? 'width' : 'height';
  }

  /**
   * `maxWidth` or `maxHeight`.
   * @private
   */
  get _dimensionMax() {
    return this.horizontal ? 'maxWidth' : 'maxHeight';
  }

  /**
   * `max-width` or `max-height`.
   * @private
   */
  get _dimensionMaxCss() {
    return this.horizontal ? 'max-width' : 'max-height';
  }

  /**
   * Toggle the opened state.
   *
   * @method toggle
   */
  toggle() {
    this.opened = !this.opened;
  }

  show() {
    this.opened = true;
  }

  hide() {
    this.opened = false;
  }

  updated(changedProperties) {
    if (changedProperties.has('horizontal')) {
      this._horizontalChanged();
    }
    if (changedProperties.has('opened')) {
      this._openedChanged();
    }
  }

  /**
   * Updates the size of the element.
   * @param {string} size The new value for `maxWidth`/`maxHeight` as css property value, usually `auto` or `0px`.
   * @param {boolean=} animated if `true` updates the size with an animation, otherwise without.
   */
  updateSize(size, animated) {
    // Consider 'auto' as '', to take full size.
    size = size === 'auto' ? '' : size;

    let willAnimate = (animated && !this.noAnimation && this.isConnected && this._desiredSize !== size);

    if (willAnimate === undefined) {
      debugger;
    }

    this._desiredSize = size;

    this._updateTransition(false);
    // If we can animate, must do some prep work.
    if (willAnimate) {
      // Animation will start at the current size.
      const startSize = this._calcSize();
      // For `auto` we must calculate what is the final size for the animation.
      // After the transition is done, _transitionEnd will set the size back to
      // `auto`.
      if (size === '') {
        this.style[this._dimensionMax] = '';
        size = this._calcSize();
      }
      // Go to startSize without animation.
      this.style[this._dimensionMax] = startSize;
      // Force layout to ensure transition will go. Set scrollTop to itself
      // so that compilers won't remove it.
      this.scrollTop = this.scrollTop;
      // Enable animation.
      this._updateTransition(true);
      // If final size is the same as startSize it will not animate.
      willAnimate = (size !== startSize);
    }
    // Set the final size.
    this.style[this._dimensionMax] = size;
    // If it won't animate, call transitionEnd to set correct classes.
    if (!willAnimate) {
      this._transitionEnd();
    }
  }

  _updateTransition(enabled) {
    this.style.transitionDuration = (enabled && !this.noAnimation) ? '' : '0s';
  }

  _horizontalChanged() {
    this.style.transitionProperty = this._dimensionMaxCss;
    const otherDimension = this._dimensionMax === 'maxWidth' ? 'maxHeight' : 'maxWidth';
    this.style[otherDimension] = '';
    this.updateSize(this.opened ? 'auto' : '0px', false);
  }

  _openedChanged() {
    this.setAttribute('aria-hidden', !this.opened);

    this._transitioning = true;
    this.classList.toggle('iron-collapse-closed', false);
    this.classList.toggle('iron-collapse-opened', false);
    this.updateSize(this.opened ? 'auto' : '0px', true);

    // Focus the current collapse.
    if (this.opened) {
      requestIdleCallback(() => { this.focus(); });
    }

    this.dispatchEvent(new CustomEvent('opened-changed', { detail: { value: this.opened }, composed: true, bubbles: true }));
  }

  _transitionEnd() {
    this.style[this._dimensionMax] = this._desiredSize;
    this.classList.toggle('iron-collapse-closed', !this.opened);
    this.classList.toggle('iron-collapse-opened', this.opened);
    this._updateTransition(false);
    // this.notifyResize();
    this._transitioning = false;
  }

  _onTransitionEnd(event) {
    if (event.composedPath().includes(this)) {
      this._transitionEnd();
    }
  }

  _calcSize() {
    return `${this.getBoundingClientRect()[this.dimension]}px`;
  }
}

customElements.define(IronCollapse.is, IronCollapse);
